import type { SupervisorFinding, SupervisorFindingStatus } from '@avisoma/shared';

/** Spec "UI": what an admin acts on. "Show all" adds rejected, abstained, dismissed, reverted and obsolete. */
export const DEFAULT_VISIBLE_STATUSES: ReadonlySet<SupervisorFindingStatus> = new Set<SupervisorFindingStatus>([
  'proposed',
  'applied',
  'reapplied',
  'stale',
]);

export const visibleFindings = (items: SupervisorFinding[], showAll: boolean): SupervisorFinding[] =>
  showAll ? items : items.filter((f) => DEFAULT_VISIBLE_STATUSES.has(f.status));

/** Versioned, so a later change of the default can reset everyone's toggle. */
export const SHOW_ALL_STORAGE_KEY = 'projectView.corrections.showAll.v1';

// Storage can be missing or throw (a private window, blocked site data); the default filter then applies.
export function readShowAll(): boolean {
  try {
    return window.localStorage.getItem(SHOW_ALL_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeShowAll(showAll: boolean): void {
  try {
    window.localStorage.setItem(SHOW_ALL_STORAGE_KEY, showAll ? '1' : '0');
  } catch {
    // The toggle then lasts for this visit only.
  }
}

/** Empty-state text. A filtered-out list names what it hides, so it never reads as "no findings". */
export const emptyFindingsMessage = (total: number, visible: number): string | null => {
  if (visible > 0) return null;
  if (total === 0) return 'No supervisor findings for this project';
  return total === 1
    ? 'No open findings. 1 finding is hidden by the filter; turn on "Show all" to see it.'
    : `No open findings. ${total} findings are hidden by the filter; turn on "Show all" to see them.`;
};
